import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { extend, QueryProvider } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  PodFooter,
  PodHeader,
  PodSection,
  PodSpacer,
  PodRowToColumn,
  PodRow,
  PodTapper,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductImage,
  BundleDataModel
} from '@thd-olt-component-react/product-pod';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { Promotion } from '@thd-olt-component-react/promotion';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { ProductSeeSimilarItems, ViewSimilarIcon } from '@thd-olt-component-react/product-pod-group';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { AisleBayPod } from '@thd-olt-component-react/aisle-bay';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { useClickInteractionListener } from '@thd-olt-functional/utils/';
import {
  PRODUCT_POD_V7_CLICK
} from '@thd-olt-component-react/product-pod/dist/components/constants';
import { sendPodTapperAnalytics } from '@thd-olt-component-react/product-pod/dist/components/productPodUtils';
import { useNodeRef } from './browse-and-search-utils';

export const CustomProductPodResponsive = ({
  itemId,
  parentId,
  storeId,
  position,
  onHover,
  hoverDelay,
  url,
  samplesLoading,
  itemRecsIndex,
  hasInStoreFilter,
  parent,
  impProductPodData,
  showProjectQuantity,
  hasSameDayFilter,
  isMobileOrConsumerApp,
  isFulfillmentFilterAndMsgEnabled,
  minimumOrderQuantity,
  showHideViewSimilarIcon,
  enablePlccPlpComponent
}) => {
  const superSkuChangedItemIdsRef = useRef([]);
  const theme = useTheme(CustomProductPodResponsive);
  const {
    hideAddToListIcon,
    hidePickupFulfillment
  } = theme.props;

  const maxLinesForProductDesc = isMobileOrConsumerApp ? 3 : 2;

  const showFulfillmentPodStore = () => {
    return isFulfillmentFilterAndMsgEnabled ? !hasSameDayFilter : true;
  };

  const showFulfillmentPodShipping = () => {
    return isFulfillmentFilterAndMsgEnabled ? !hasInStoreFilter : true;
  };

  const minOrderQty = minimumOrderQuantity > 0 ? minimumOrderQuantity : undefined;

  // -----------------
  // Analytics - New-Tab Click Tracker
  // -----------------
  const [productPodNodeRef, productPodNode] = useNodeRef();

  const productData = {
    podAction: 'product pod',
    podAnchorSku: itemId,
    target: '_self',
    parent: 'plp'
  };

  const sendAnalytics = () => {
    if (!window) return;
    window.LIFE_CYCLE_EVENT_BUS.trigger(PRODUCT_POD_V7_CLICK, productData); // analytic & ad-server
  };

  useClickInteractionListener({
    callbackFn: sendAnalytics,
    targetNode: productPodNode,
    itemId,
    sponsoredId: itemId,
    trackSource: '',
    slotId: '',
    position: '',
    plaLocation: ''
  });

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      className="product-pod"
      data-testid="product-pod"
    >
      <ProductPod
        analyticsData={{ parent, position }}
        analyticImpressionData={impProductPodData}
        onHover={onHover}
        hasSSKU
        hoverDelay={hoverDelay}
        itemId={itemId}
        storeId={storeId}
        padding="sui-p-2 sm:sui-pt-2 sm:sui-px-4 sm:sui-pb-4"
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-10"
              href={url}
              aria-label="Link"
            />
            <div className="sui-flex sui-flex-col sui-h-full">
              <PodSpacer padding={['BOTTOM']}>
                <PodHeader noPadding>
                  <PodRow noPadding>
                    <ProductBadge itemId={pod.itemId} storeId={storeId} />
                    <ProductSponsored itemId={pod.itemId} position={position} />
                  </PodRow>
                  {
                    !hideAddToListIcon
                    && (
                      <PodTapper fitWidth zIndex>
                        <PodRow rowReverse noPadding>
                          <AddToList itemId={pod.itemId} showIconButton storeId={storeId} />
                        </PodRow>
                      </PodTapper>
                    )
                  }
                </PodHeader>
              </PodSpacer>
              <PodSection
                id="Slot-2"
                display="sui-grid"
                gap="sui-gap-0"
                noPadding
              >
                <PodRowToColumn
                  noPadding
                  gap="sui-gap-x-4 md:sui-gap-x-0"
                >
                  <PodTapper zIndex>
                    <ProductImage
                      itemId={pod.itemId}
                      showSecondaryImage={false}
                      dualImageSwipe={false}
                    />
                  </PodTapper>
                  <PodSection
                    display="sui-block sui-w-full"
                    noPadding
                    padding="sui-pt-2"
                    grid="sui-col-start-1 sui-col-end-3 md:sui-order-2"
                    order="sui-order-3"
                    columnAlign
                  >
                    <PodTapper
                      zIndex
                      aPointer
                      buttonPointer
                      cursorPointer
                      handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                    >
                      <ProductSample itemId={pod.itemId} />
                      <SuperSkuMiniSwatches
                        storeId={storeId}
                        itemId={pod.itemId}
                        onChange={(clickData) => {
                          if (!superSkuChangedItemIdsRef.current.includes(clickData.itemId)) {
                            superSkuChangedItemIdsRef.current = [
                              ...superSkuChangedItemIdsRef.current,
                              clickData.itemId
                            ];
                          }
                          pod.onChange(clickData);
                        }}
                        onHover={pod.onSSKUHover}
                        componentImpressionData={{
                          name: 'ProductPod',
                          component: 'ProductPod',
                          position: position + 1,
                          type: 'product',
                          anchorSku: parentId
                        }}
                        isImpressionsRequired
                        hideComponentWhenEmpty
                      />
                      <ProductServiceAddOns itemId={pod.itemId} />
                    </PodTapper>
                  </PodSection>
                  {showHideViewSimilarIcon?.fsisViewSimilarButtonPositionAboveABTest && (
                    <PodSection
                      display="sui-block sui-w-full"
                      noPadding
                      grid="sui-col-start-1 sui-col-end-3 md:sui-order-2"
                      order="sui-order-3"
                      columnAlign
                      zIndex
                      fitWidth
                    >
                      <div className="sui-flex sui-items-center sui-z-20">
                        <ViewSimilarIcon
                          itemId={pod.itemId}
                          products={showHideViewSimilarIcon?.products}
                          storeId={storeId}
                        />
                      </div>
                    </PodSection>
                  )}
                  <PodSection
                    display="sui-grid"
                    gap="sui-gap-0"
                    order="sui-order-2 md:sui-order-3"
                    padding="sui-mb-auto sui-px-1"
                    alignTop
                    noPadding
                  >
                    <PodTapper fitWidth zIndex>
                      <PodSpacer margin="md:sui-mt-1">
                        <ProductHeader
                          brand="inline"
                          brandLineHeight="tight"
                          brandFontSize="sm"
                          productNameLineHeight="tight"
                          itemId={pod.itemId}
                          brandMaxLine={1}
                          titleMaxLine={maxLinesForProductDesc}
                          disableShopThisCollection
                        />
                      </PodSpacer>
                    </PodTapper>
                    <PodTapper fitWidth zIndex>
                      <ProductRating itemId={pod.itemId} />
                    </PodTapper>
                    <ProductIdentifier itemId={pod.itemId} type="model" />
                  </PodSection>
                </PodRowToColumn>
                <PodSection noPadding alignTop columnAlign padding="sui-px-1">
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer
                      padding={['T2', 'BOTTOM']}
                      disable={pod.productSubType === 'HDQC'}
                    >
                      <Price
                        clsRemediation={{ placeholders: true, preservePlaceholders: true }}
                        disableOuterSpacing
                        itemId={pod.itemId}
                        large={false}
                        storeId={storeId}
                        displayEachUom={false}
                        showProjectPrice
                        onSavingsCenterToggle={pod.onSavingsCenterToggle}
                        hideForProductTypes={['HDQC']}
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
                {enablePlccPlpComponent && (
                  <PodSection noPadding columnAlign padding="sui-px-1">
                    <PodTapper zIndex aPointer buttonPointer spanPointer>
                      <PodSpacer
                        padding={['T2', 'BOTTOM']}
                        disable={pod.productSubType === 'HDQC'}
                      >
                        <ConsumerCardMessaging
                          itemId={pod.itemId}
                          clsRemediation={{
                            placeholders: false,
                            preservePlaceholders: true
                          }}
                          isPlp
                          applyNowCampaignCode="~P~U~O~3~9~ZZZ0~AI~HD~ZZ"
                          paymentEstimatorCampaignCode="~P~U~J~6~9~ZZZ0~AI~HD~ZZ"
                        />
                      </PodSpacer>
                    </PodTapper>
                  </PodSection>
                )}
                <PodSection noPadding alignTop columnAlign padding="sui-px-1">
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer
                      padding={['T2', 'BOTTOM']}
                    >
                      <Promotion
                        itemId={pod.itemId}
                        hideConditionalPromoDescription
                        pageType={parent.toUpperCase()}
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
              </PodSection>
              <PodFooter noPadding margin="sui-mt-auto">
                <PodSection display="sui-grid" padding="sui-px-1" noPadding alignEnd>
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer
                      padding={['B2']}
                      disable={pod.productSubType === 'HDQC' || pod.productSubType === 'SFI'}
                    >
                      {
                        !hidePickupFulfillment && showFulfillmentPodStore()
                        && (
                          <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} />
                        )
                      }
                      {
                        showFulfillmentPodShipping()
                        && (
                          <FulfillmentPodShipping
                            itemId={pod.itemId}
                            storeId={storeId}
                            hasSameDayFilter={hasSameDayFilter}
                            showFasterPodSpacing
                          />
                        )
                      }
                    </PodSpacer>
                    {isMobileOrConsumerApp && (
                      <PodSpacer>
                        <AisleBayPod
                          itemId={pod.itemId}
                          clsRemediation={{
                            placeholders: true,
                            preservePlaceholders: false
                          }}
                          storeId={storeId}
                          padding="sui-mb-2"
                        />
                      </PodSpacer>
                    )}
                  </PodTapper>
                </PodSection>
                <PodSection display="sui-grid" gap="sui-gap-0" noPadding alignEnd>
                  <PodTapper zIndex>
                    <PodRow noPadding>
                      <div className="sui-flex-1">
                        <ProductATC
                          hasInStoreFilter={hasInStoreFilter}
                          showProjectQuantity={showProjectQuantity}
                          storeId={storeId}
                          itemId={pod.itemId}
                          checkGeneric
                          outline
                          quantity={minOrderQty}
                        />
                        <ProductSeeSimilarItems
                          itemId={pod.itemId}
                          position={itemRecsIndex}
                          productPodRef={pod.ref}
                          storeId={storeId}
                        />
                        <PodSpacer disable={pod.productSubType !== 'HDQC'}>
                          <NonBuyableQuoteCenter
                            itemId={pod.itemId}
                            checkForProductSubType={['HDQC']}
                          />
                        </PodSpacer>
                      </div>
                      {showHideViewSimilarIcon?.fsisViewSimilarButtonPositionBelowABTest && (
                        <div className="sui-flex sui-items-center sui-pl-2">
                          <ViewSimilarIcon
                            itemId={pod.itemId}
                            products={showHideViewSimilarIcon?.products}
                            storeId={storeId}
                          />
                        </div>
                      )}
                      <div className="sui-flex sui-items-center sui-self-center">
                        <OrderSample
                          disableSampleCall={
                            samplesLoading
                            || (pod.itemId !== itemId && !superSkuChangedItemIdsRef.current.includes(pod.itemId))
                          }
                          itemId={pod.itemId}
                          showIconOnly
                          link
                        />
                      </div>
                    </PodRow>
                  </PodTapper>
                </PodSection>
              </PodFooter>
            </div>
          </>
        )}
      />
    </div>
  );
};

CustomProductPodResponsive.displayName = 'CustomProductPodResponsive';

CustomProductPodResponsive.themeProps = {
  hideAddToListIcon: PropTypes.bool,
  hidePickupFulfillment: PropTypes.bool
};

CustomProductPodResponsive.defaultThemeProps = {
  hideAddToListIcon: false,
  hidePickupFulfillment: false
};

CustomProductPodResponsive.propTypes = {
  itemId: PropTypes.string.isRequired,
  parentId: PropTypes.string,
  itemRecsIndex: PropTypes.number.isRequired,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  onHover: PropTypes.func.isRequired,
  hoverDelay: PropTypes.number.isRequired,
  hasInStoreFilter: PropTypes.bool.isRequired,
  samplesLoading: PropTypes.bool.isRequired,
  url: PropTypes.string.isRequired,
  impProductPodData: PropTypes.shape({}),
  parent: PropTypes.string,
  showProjectQuantity: PropTypes.bool,
  hasSameDayFilter: PropTypes.bool,
  isMobileOrConsumerApp: PropTypes.bool,
  isFulfillmentFilterAndMsgEnabled: PropTypes.bool,
  minimumOrderQuantity: PropTypes.number,
  showHideViewSimilarIcon: PropTypes.shape({
    fsisViewSimilarButtonPositionAboveABTest: PropTypes.bool,
    fsisViewSimilarButtonPositionBelowABTest: PropTypes.bool,
    products: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  enablePlccPlpComponent: PropTypes.bool
};

CustomProductPodResponsive.defaultProps = {
  impProductPodData: null,
  parent: 'plp',
  showProjectQuantity: false,
  hasSameDayFilter: false,
  isMobileOrConsumerApp: false,
  isFulfillmentFilterAndMsgEnabled: false,
  minimumOrderQuantity: 0,
  parentId: null,
  showHideViewSimilarIcon: null,
  enablePlccPlpComponent: false
};

CustomProductPodResponsive.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductServiceAddOns,
  AddToList,
  Promotion,
  Price,
  SuperSkuMiniSwatches,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductHeader,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductSeeSimilarItems,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  NonBuyableQuoteCenter,
  OrderSample,
  ConsumerCardMessaging,
  PriceClearanceDataModel,
  BundleDataModel,
  ViewSimilarIcon
);
